import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Country } from 'react-phone-number-input';
import {
  formatPhoneNumber as formatPhoneNumberField,
  parsePhoneNumber,
  isValidPhoneNumber,
  getCountryCallingCode,
  getCountries,
} from 'react-phone-number-input';
import Input from 'react-phone-number-input/input'; //change to PhoneInput to support international numbers
import 'react-phone-number-input/style.css';
import type { InputProps } from 'antd';
import { Input as AntdInput } from 'antd';

const ShipiumInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const inputRef = useRef<any>(null);

  useImperativeHandle(ref, () => {
    return inputRef?.current?.input;
  }, []);
  return <AntdInput ref={inputRef} {...props} />;
});

interface PhoneFieldProps {
  value?: string;
  onChange?: (value?: string) => void;
  countryCode?: Country;
  [key: string]: any;
}
// adding + because either can be stored in partner-management-service
const COUNTRY_BY_CALLING_CODE: Record<string, string> = (() => {
  const dict: Record<string, string> = {};
  for (const val of getCountries()) {
    const key = getCountryCallingCode(val);
    if (key === '1') {
      // hardcoded rules because a single phone code
      // can map to multiple countries
      dict[key] = 'US';
      dict['+1'] = 'US';
    } else {
      dict[key] = val;
      dict[`+${key}`] = val;
    }
  }
  return dict;
})();

export const getCountryFromCallingCode = (callingCode?: string) => {
  if (!callingCode) {
    return;
  }
  // because existing records may have 2 char
  // country codes 'US' 'MX' etc.
  // we default to what was passed
  return COUNTRY_BY_CALLING_CODE[callingCode] ?? callingCode;
};
export const getCountryCallingCodeDefaultUS = (countryCode?: Country) => {
  return countryCode ? `+${getCountryCallingCode(countryCode)}` : '+1';
};
export const validatePhoneNumber = (value: string, countryCode?: Country) => {
  return isValidPhoneNumber(getCountryCallingCodeDefaultUS(countryCode) + value);
};

export const formatPhoneNumber = (value?: string, countryCode?: Country) => {
  return value ? formatPhoneNumberField(getCountryCallingCodeDefaultUS(countryCode) + value) : value;
};

export const removeCountryCode = (value: string, countryCode?: Country) => {
  return parsePhoneNumber(value, countryCode)?.nationalNumber;
};

const PhoneField = ({ value, onChange = () => {}, countryCode }: PhoneFieldProps) => {
  //partner-management-service does not store country code
  const handleChange = (value?: string | undefined) => {
    onChange(value ? removeCountryCode(value) : undefined);
  };

  return (
    <Input
      value={value ? getCountryCallingCodeDefaultUS(countryCode) + value : ''}
      onChange={handleChange}
      placeholder="Enter phone number"
      className="ant-input"
      country={countryCode ?? 'US'}
      inputComponent={ShipiumInput}
      defaultCountry={'US'}
    />
  );
};

export default PhoneField;
